const clean = value => String(value ?? '').replace(/\\r?\\n/g, ' ').replace(/\s+/g, ' ').trim();
export const normalize = value => clean(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const privateField = /(^| )(stock|precio|price|cost|coste|supplier|pvp|msrp|canon|margen|margin|published|updated|ref mcr|ref visiotech)( |$)/;
const labels = {poe:'PoE',proteccion:'Protecci\u00f3n',wifi:'Wi-Fi',audio:'Audio',alarmas:'Alarmas',almacenamiento_interno:'Almacenamiento interno',alcance_ir:'Alcance infrarrojo',lente:'Lente',resolucion_maxima:'Resoluci\u00f3n m\u00e1xima',categoria:'Categor\u00eda',marca:'Marca',wi_fi:'Wi-Fi',alimentacion_poe:'Alimentaci\u00f3n PoE',velocidad_wi_fi:'Velocidad Wi-Fi',poe_out:'PoE Out',estandar_wi_fi:'Est\u00e1ndar Wi-Fi'};
const aliases = new Map([
  ['numero de nucleos de procesador','nucleos'],['nucleos de procesador','nucleos'],
  ['socket de procesador','socket'],['resolucion maxima','resolucion'],['resolucion max','resolucion'],
  ['alimentacion poe','poe'],['proteccion ip','proteccion'],['codigo ip international protection','proteccion'],
  ['wifi estandar','wi fi'],['wifi','wi fi'],['rango ir','alcance infrarrojo'],['alcance ir','alcance infrarrojo'],
  ['numero de filamentos de procesador','hilos del procesador']
]);
const keyOf = name => aliases.get(normalize(name)) || normalize(name);
const displayValue = value => /^(y|yes|si|s\u00ed|true)$/i.test(clean(value)) ? 'S\u00ed' : /^(n|false)$/i.test(clean(value)) ? 'No' : clean(value);

export function parseSpecHtml(html, Parser = DOMParser) {
  if (!html) return [];
  // The supplier exports an unterminated, empty style attribute on some tables.
  const repaired = String(html).replace(/\sstyle="(?=>)/gi, '');
  const doc = new Parser().parseFromString(`<html><body>${repaired}</body></html>`, 'text/html');
  doc.querySelectorAll('script,style,iframe,object').forEach(el => el.remove());
  doc.querySelectorAll('br').forEach(el => el.replaceWith(doc.createTextNode(' / ')));
  const rows = [];
  doc.querySelectorAll('tr').forEach(tr => {
    const cells = [...tr.children].filter(el => /^(TD|TH)$/.test(el.tagName));
    if (cells.length >= 2) rows.push({name:clean(cells[0].textContent),value:cells.slice(1).map(el=>clean(el.textContent)).join(' / ')});
  });
  doc.querySelectorAll('li').forEach(li => {
    if (li.querySelector('li')) return;
    const text = clean(li.textContent), colon = text.indexOf(':');
    if (colon > 0) rows.push({name:text.slice(0,colon),value:text.slice(colon+1).trim()});
    else if (text) rows.push({name:'Caracter\u00edstica',value:text});
  });
  if (!rows.length && clean(doc.body.textContent)) rows.push({name:'Informaci\u00f3n t\u00e9cnica',value:clean(doc.body.textContent)});
  return rows;
}

export function objectRows(data, prefix = '') {
  if (typeof data === 'string') { try { return objectRows(JSON.parse(data),prefix); } catch { return []; } }
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data)) return data.flatMap(item => {
    if (item && typeof item === 'object' && ('name' in item || 'label' in item) && 'value' in item) return [{name:String(item.name ?? item.label),value:Array.isArray(item.value)?item.value.join(', '):item.value}];
    return objectRows(item,prefix);
  });
  return Object.entries(data).flatMap(([name,value]) => {
    if (privateField.test(normalize(name))) return [];
    if (value && typeof value === 'object') {
      if (Array.isArray(value) && value.every(v=>typeof v!=='object')) return [{name:[prefix,name].filter(Boolean).join(' / '),value:value.join(', ')}];
      return objectRows(value, name === 'specs' || name === 'specifications' ? prefix : [prefix,name].filter(Boolean).join(' / '));
    }
    return [{name:[prefix,name].filter(Boolean).join(' / '),value}];
  });
}

export function mergeRows(rows) {
  const map = new Map();
  for (const row of rows) {
    const rawName = clean(row.name);
    const name = labels[rawName.replaceAll('-','_')] || rawName.replace(/[_-]+/g,' ').replace(/^[a-z]/, letter => letter.toUpperCase());
    const value = displayValue(row.value);
    if (!name || !value || privateField.test(normalize(name))) continue;
    const key = keyOf(name);
    if (!map.has(key)) map.set(key,{name,value,values:new Set([normalize(value)])});
    else {
      const existing = map.get(key);
      if (!existing.values.has(normalize(value))) { existing.values.add(normalize(value)); existing.value += ` / ${value}`; }
    }
  }
  return [...map.values()].map(({name,value})=>({name,value}));
}

export function groupFor(name) {
  const key = normalize(name);
  if (/tecnologia|intel.*(boost|step|shift|vpro|execution|director|sure|select)|virtualiza|avx|aes|instrucci|execute|hyper threading|modo.*mbe/.test(key)) return 'Funciones y tecnolog\u00edas';
  if (/memoria|almacena|disco|hdd|ssd|cache|(^| )ecc( |$)|sd |bahia/.test(key)) return 'Memoria y almacenamiento';
  if (/procesador|cpu|nucleos|socket|litografia|frecuencia|filamentos|hilos|upi|escalabilidad/.test(key)) return 'Procesador y rendimiento';
  if (/imagen|resolucion|lente|optica|ir$|infrarro|dori|dia noche|stream|compresion|pantalla|brillo|pixel|(^| )vision/.test(key)) return 'Imagen y \u00f3ptica';
  if (/audio|microfono|altavoz/.test(key)) return 'Audio';
  if (/red|ethernet|wi fi|wifi|banda|bluetooth|interfaz|interoperab|puerto|pci |conector|acceso remoto|onvif|sfp/.test(key)) return 'Conectividad e interfaces';
  if (/alimenta|poe|potencia|tdp|consumo|voltaje|bateria|energia/.test(key)) return 'Alimentaci\u00f3n y consumo';
  if (/ivs|analitica|alarma|seguridad/.test(key)) return 'Funciones y tecnolog\u00edas';
  if (/dimension|tamano|peso|material|proteccion|temperatura|temp funcionamiento|tcase|montaje|refrigerador|caja|rack|color/.test(key)) return 'Dise\u00f1o e instalaci\u00f3n';
  return 'Informaci\u00f3n general';
}

function element(doc, tag, className, text) {
  const el = doc.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

export function organizeDescription(root) {
  if (!root || root.dataset.organized) return;
  root.dataset.organized = 'true';
  const doc = root.ownerDocument;
  function decode(node) {
    for (const child of [...node.childNodes]) {
      if (child.nodeType === 3 && /\\[nr]/.test(child.textContent)) child.textContent = child.textContent.replace(/\\r\\n|\\n|\\r/g,'\n');
      else if (child.nodeType === 1) decode(child);
    }
  }
  decode(root);
  for (const node of [...root.childNodes]) {
    if (node.nodeType === 1 && /^(B|STRONG)$/.test(node.tagName)) {
      const h = element(doc,'h3','',node.textContent); node.replaceWith(h);
    } else if (node.nodeType === 3 && clean(node.textContent)) {
      const paragraphs = node.textContent.split(/\n\s*\n/).filter(clean).map(text=>element(doc,'p','',text.trim()));
      node.replaceWith(doc.createTextNode('\n'),...paragraphs.flatMap(p=>[p,doc.createTextNode('\n')]));
    } else if (node.nodeType === 1 && node.tagName === 'BR') node.remove();
  }
  root.querySelectorAll('[style]').forEach(node=>node.removeAttribute('style'));
  const headings = [...root.children].filter(el=>/^H[2-6]$/.test(el.tagName));
  if (headings.length < 5) return;
  const fragment = doc.createDocumentFragment();
  let body = fragment, index = 0;
  for (const node of [...root.childNodes]) {
    if (node.nodeType === 1 && /^H[2-6]$/.test(node.tagName)) {
      const detail = element(doc,'details','netisec-description-topic');
      if (index++ === 0) detail.setAttribute('open','');
      const summary = element(doc,'summary','');
      summary.append(element(doc,'h3','',node.textContent)); detail.append(summary);
      body = element(doc,'div','netisec-description-topic__body'); detail.append(body); fragment.append(detail);
    } else body.append(node);
  }
  root.replaceChildren(fragment);
}

export function renderSpecifications(root, rows) {
  const doc = root.ownerDocument, target = root.querySelector('[data-spec-groups]');
  if (!target) return;
  const grouped = new Map();
  rows.forEach(row=>{const group=groupFor(row.name);if(!grouped.has(group))grouped.set(group,[]);grouped.get(group).push(row);});
  const order = ['Procesador y rendimiento','Imagen y \u00f3ptica','Memoria y almacenamiento','Conectividad e interfaces','Audio','Alimentaci\u00f3n y consumo','Dise\u00f1o e instalaci\u00f3n','Funciones y tecnolog\u00edas','Informaci\u00f3n general'];
  const fragment=doc.createDocumentFragment();let index=0;
  for(const name of order){
    if(!grouped.has(name))continue;
    const group=element(doc,'details','netisec-spec-group');if(index++<2)group.setAttribute('open','');
    const summary=element(doc,'summary','');summary.append(element(doc,'h3','',name),element(doc,'span','netisec-spec-group__count',String(grouped.get(name).length)));
    const table=element(doc,'table','netisec-technical-table');table.setAttribute('aria-label',name);const tbody=element(doc,'tbody','');
    for(const row of grouped.get(name)){
      const tr=element(doc,'tr','');tr.dataset.search=normalize(`${row.name} ${row.value}`);
      const th=element(doc,'th','',row.name);th.setAttribute('scope','row');tr.append(th,element(doc,'td','',row.value));tbody.append(tr);
    }
    table.append(tbody);group.append(summary,table);fragment.append(group);
  }
  target.replaceChildren(fragment);
  root.querySelector('[data-spec-count]').textContent=`${rows.length} datos t\u00e9cnicos`;
  root.querySelector('.netisec-spec-tools').hidden=rows.length<8;
  const groups=[...target.querySelectorAll('details')], expand=root.querySelector('[data-spec-expand]');
  expand.addEventListener('click',()=>{const opening=groups.some(group=>!group.open);groups.forEach(group=>group.open=opening);expand.lastChild.textContent=opening?'Contraer todo':'Expandir todo';});
  const initial=groups.map(group=>group.open);
  root.querySelector('[data-spec-search]').addEventListener('input',event=>{
    const query=normalize(event.target.value);let matches=0;
    groups.forEach((group,i)=>{
      let visible=0;group.querySelectorAll('tr').forEach(row=>{row.hidden=!row.dataset.search.includes(query);if(!row.hidden)visible++;});
      group.hidden=!visible;group.open=query?true:initial[i];matches+=visible;
    });
    root.querySelector('.netisec-spec-empty').hidden=matches>0;
    root.querySelector('[data-spec-count]').textContent=query?`${matches} de ${rows.length} datos t\u00e9cnicos`:`${rows.length} datos t\u00e9cnicos`;
  });
}

export function initializeInformation(root) {
  if (root.dataset.ready) return;
  root.dataset.ready='true';organizeDescription(root.querySelector('[data-organize-description]'));
  const payload=root.querySelector('[data-spec-payload]');if(!payload)return;
  let data;try{data=JSON.parse(payload.textContent);}catch{return;}
  const custom=[...root.querySelectorAll('[data-spec-row]')].map(row=>({name:row.querySelector('th').textContent,value:row.querySelector('td').textContent}));
  const rows=mergeRows([...parseSpecHtml(data.mcrHtml),...parseSpecHtml(data.visiotechHtml),...objectRows(data.mcrAttributes),...objectRows(data.visiotechAttributes),...objectRows(data.additional),...custom]);
  if(!rows.length)return;renderSpecifications(root,rows);
  const highlights=root.ownerDocument.querySelector('[data-product-highlights]');
  if(highlights){
    const priority=/nucleos|socket|frecuencia del procesador$|resolucion|lente$|wifi|wi fi|bandas|capacidad|potencia total|almacenamiento|poe|pantalla|puertos|memoria ram/;
    const chosen=rows.filter(row=>priority.test(normalize(row.name))).slice(0,4);
    if(chosen.length){const dl=element(root.ownerDocument,'dl','netisec-pdp-key-specs');chosen.forEach(row=>{const item=element(root.ownerDocument,'div','');item.append(element(root.ownerDocument,'dt','',row.name),element(root.ownerDocument,'dd','',row.value));dl.append(item);});highlights.replaceChildren(dl);highlights.hidden=false;}
  }
}

if(typeof customElements!=='undefined'&&!customElements.get('product-information-organizer')){
  customElements.define('product-information-organizer',class extends HTMLElement{connectedCallback(){initializeInformation(this);}});
}
